import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, BadgeCheck, CheckCircle2, CreditCard, Shield, Calendar, AlertCircle, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';

export default function FlightLayawayVsBNPL() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonicalUrl = 'https://defera.app/guides/flight-layaway-vs-bnpl';
  const metaTitle = "Flight Layaway vs BNPL: What's the Difference? (2026 Guide)";
  const metaDescription = 'Compare flight layaway vs buy now, pay later (BNPL): when your booking is confirmed, fees, credit checks, and which option fits your budget and travel timeline.';

  const quickCompare = [
    {
      dimension: 'When your booking is confirmed',
      layaway: 'Usually after the final payment. Defera: trip price secured once your booking is approved; airline confirmation number once the plan is paid in full',
      bnpl: 'Usually right away at checkout'
    },
    {
      dimension: 'How it works',
      layaway: 'Deposit, then installments before you fly (Defera: weekly or bi-weekly, with price protection)',
      bnpl: 'A lender pays the merchant now; you repay the lender over time'
    },
    {
      dimension: 'Fees / interest',
      layaway: 'Often fee-based; commonly no interest (Defera: no interest, fees included in the trip total)',
      bnpl: 'Many pay-in-4 plans are 0%; longer terms may add APR'
    },
    {
      dimension: 'Credit check',
      layaway: 'No credit check (Defera); often none or minimal elsewhere (varies by provider)',
      bnpl: 'Often a soft check; eligibility varies'
    },
    {
      dimension: 'Spending limits',
      layaway: 'No credit line: you pay toward one trip',
      bnpl: 'Your approved amount may not cover the full trip cost'
    },
    {
      dimension: 'Best for',
      layaway: 'Planning ahead, avoiding credit-based lending',
      bnpl: 'Booking immediately with a fixed schedule'
    }
  ];

  const decisionChecklist = [
    {
      title: 'You need your booking confirmed today',
      best: 'BNPL',
      why: 'Most BNPL plans confirm your booking at checkout. With Defera, your trip price is secured once your booking is approved, and your airline confirmation number arrives once the plan is paid in full.'
    },
    {
      title: 'You want to avoid credit-card style borrowing',
      best: 'Layaway-style plans',
      why: 'Layaway-style plans collect your payments before you fly instead of lending you the money, so there is no loan to repay.'
    },
    {
      title: 'You care most about fee transparency',
      best: 'Plans with upfront schedules and total cost',
      why: 'Choose providers that show due dates, total cost, and policies before checkout.'
    },
    {
      title: 'Your travel date is weeks away',
      best: 'Layaway or longer installment schedules',
      why: 'More runway means smaller payments and less pressure on cash flow. Defera trips, for example, must depart at least 22 days out.'
    }
  ];

  const faqs = [
    {
      question: 'Is flight layaway the same as BNPL?',
      answer: 'No. With BNPL, a lender pays the airline or travel site at checkout, so your booking is confirmed right away and you repay the lender over time. Traditional layaway holds the trip while you pay and confirms it after the final payment. Defera secures your agreed-upon trip price once your booking is approved and protects it from future airfare increases while you pay; your airline confirmation number arrives once the plan is paid in full.'
    },
    {
      question: 'Which option is cheaper: layaway or BNPL?',
      answer: 'It depends on the provider and term length. Short pay-in-4 BNPL can be 0% if paid on time, while longer BNPL terms may add APR. Layaway is often interest-free but may include service fees. Always compare total cost before confirming.'
    },
    {
      question: 'Do I need a credit check for BNPL flights?',
      answer: 'Many BNPL providers use a soft check or internal risk signals, but policies vary by market and purchase size. Some options are designed to minimize or avoid credit checks.'
    },
    {
      question: 'What happens if I miss a payment?',
      answer: 'You may face late fees or plan cancellation depending on the provider. To reduce risk, use autopay, keep a buffer, and choose a schedule that matches your paycheck cadence.'
    },
    {
      question: 'How does Defera fit into layaway vs BNPL?',
      answer: 'Defera is a layaway-style payment plan, not a loan: you pay a deposit, then automatic weekly or bi-weekly payments, with no credit check and no interest. Once your booking is approved, your agreed-upon trip price is protected from future airfare increases while you stay in good standing, and your airline confirmation number arrives once the plan is paid in full.'
    }
  ];

  return (
    <>
      <PageSeo
        path="/guides/flight-layaway-vs-bnpl"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: metaTitle,
            description: metaDescription,
            author: { '@type': 'Organization', name: 'Defera' },
            publisher: {
              '@type': 'Organization',
              name: 'Defera',
              logo: {
                '@type': 'ImageObject',
                url: 'https://defera.app/defera-transparent.png'
              }
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer }
            }))
          },
        ]}
      />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero */}
        <section className="pt-28 pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-white/45 text-xs font-normal uppercase tracking-wider">
                  2026 Guide
                </span>
                <span className="text-white/40 text-xs">Updated November 2026</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                Flight Layaway vs BNPL: What to Choose (and Why)
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                If you want to book flights on installments, two terms come up constantly: <strong className="text-white">BNPL</strong> and
                <strong className="text-white"> layaway</strong>. They both spread payments, but they differ in when your booking is confirmed, risk,
                and total cost. This guide gives you a clear decision framework.
              </p>
            </motion.div>
          </div>
        </section>

        {/* AEO: Quick Answer */}
        <section className="pb-12 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1d1d1f] border border-white/[0.08] rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-xl md:text-2xl font-bold text-white mb-3">Quick answer</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    <strong className="text-white">BNPL</strong> usually confirms your booking right away, then you repay the lender over time.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    <strong className="text-white">Layaway</strong> typically holds the itinerary while you pay, then confirms it after the final payment. Defera secures your trip price once your booking is approved, and your airline confirmation number arrives once the plan is paid in full.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-white/55 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">
                    Choose based on <strong className="text-white">when you need your confirmation</strong>, <strong className="text-white">fee clarity</strong>, and how soon you travel.
                  </span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Definitions */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Definitions (in plain English)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <CreditCard className="w-6 h-6 text-white/70" />
                    <h3 className="text-white font-semibold text-lg">BNPL for flights</h3>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">
                    You book now, and your booking is typically confirmed right away. You repay the BNPL lender in fixed installments (often every two weeks, or monthly).
                  </p>
                </div>
                <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-6 h-6 text-white/55" />
                    <h3 className="text-white font-semibold text-lg">Layaway for travel</h3>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">
                    You reserve an itinerary and pay over time, finishing before you fly. Traditional layaway may confirm the booking only after the final payment (provider rules vary). With Defera, you pay a deposit and then weekly or bi-weekly payments; your agreed-upon trip price is secured once your booking is approved and protected from future airfare increases while you pay, and your airline confirmation number arrives once the plan is paid in full.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-5 flex items-start gap-4">
                <AlertCircle className="w-5 h-5 text-white/90 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm">
                  <strong className="text-white/90">Important:</strong> Always confirm <strong className="text-white">when your booking is confirmed</strong> and the total cost.
                  Some providers reserve pricing differently, and policies vary by itinerary and market.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Comparison table */}
        <section className="py-12 px-6 bg-zinc-950/50 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Layaway vs BNPL comparison</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-white font-semibold">Dimension</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">Layaway</th>
                      <th className="text-left py-3 px-4 text-white font-semibold">BNPL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickCompare.map((row, idx) => (
                      <tr key={idx} className="border-b border-white/10">
                        <td className="py-3 px-4 text-white/90 font-medium">{row.dimension}</td>
                        <td className="py-3 px-4 text-white/80">{row.layaway}</td>
                        <td className="py-3 px-4 text-white/80">{row.bnpl}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Decision framework */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">How to choose (fast)</h2>
              <div className="space-y-4">
                {decisionChecklist.map((item, index) => (
                  <div key={index} className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <BadgeCheck className="w-5 h-5 text-white/70 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                        <p className="text-white/80 text-sm mb-2">
                          <strong className="text-white">Best fit:</strong> {item.best}
                        </p>
                        <p className="text-white/80 text-sm">{item.why}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Defera CTA */}
        <section className="py-14 px-6 bg-zinc-950/50 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-white/70" />
                <h2 className="text-2xl md:text-3xl font-bold text-white">A traveler-first way to pay over time</h2>
              </div>
              <p className="text-white/80 leading-relaxed mb-6">
                Defera is a layaway-style plan with no credit check and no interest. Pay a deposit, then choose weekly or bi-weekly payments that fit your cash flow;
                the trip total at checkout already includes every fee, and you manage everything in one place.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/search')} className="bg-white hover:bg-white/90 text-black px-6 py-3 rounded-full font-semibold text-sm">
                  Search Flights
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/guides/flight-installments-bnpl-payment-options')}
                  className="border border-white/15 text-white hover:bg-white/10 px-6 py-3 rounded-full font-semibold text-sm"
                >
                  No-credit-card options
                  <Wallet className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="bg-[#1d1d1f] border border-white/[0.08] rounded-xl px-6 py-5 hover:border-white/[0.12] transition-colors">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Related guides</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/guides/lock-flight-price-with-deposit" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  How to lock in a flight price with a deposit
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-installments-bnpl-payment-options" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Book flights on installments without a credit card
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/defera-vs-affirm-klarna-uplift" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Defera vs Affirm, Klarna & Uplift
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
              <li>
                <Link to="/guides/flight-payment-plans-no-credit-check" className="inline-flex items-center gap-2 text-white/80 hover:text-white underline underline-offset-2">
                  Flight payment plans with no credit check
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            </ul>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
